import { useCallback, useState } from 'react'
import { TMedEventUI } from 'src/types/med-event'
import { CurecallBanner } from '@honestica/curecall-ui'
import SelectLocation from 'src/components/followup/med-events/fields/select-location.component'
import AppointmentDatePicker from 'src/components/followup/med-events/fields/appointment-date-picker.component'
import SelectMedicalStaffMember from 'src/components/select-medical-staff-member/select-medical-staff-member.component'
import { twMerge } from 'src/utils/tw-merge.util'
import { useTranslation } from 'react-i18next'
import { getTranslatedLabel } from 'src/utils/locale.util'
import ConvocationDatePicker from '../followup/med-events/fields/convocation-date-picker.component'
import { TPatient } from 'src/types/patient'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { TNavigationLocales } from 'src/types/locales'
import { useTimezonePreference } from 'src/providers/timezone-preference.provider'
import TimezoneInputSelector from 'src/components/timezone-input-selector/timezone-input-selector.component'

interface Props {
  selectedMedEvent: TMedEventUI
  patient: TPatient
  isEditing: boolean
  defaultLocationId?: number
  errorClassName?: string
}
const MedEventFields = ({
  selectedMedEvent,
  patient,
  defaultLocationId,
  isEditing,
  errorClassName,
}: Props) => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language as TNavigationLocales
  const { getMedEventLabelById } = useCurrentProject()
  const { hasTimezoneMismatch, isProjectTimezoneSelected, projectTimezone, browserTimezone } =
    useTimezonePreference()
  const [isProjectTz, setIsProjectTz] = useState(hasTimezoneMismatch && isProjectTimezoneSelected)

  const handleToggleTimezone = useCallback(() => {
    setIsProjectTz((prev) => !prev)
  }, [])

  const getTimezone = () => {
    if (!hasTimezoneMismatch) return undefined
    return isProjectTz ? projectTimezone : browserTimezone
  }
  const timezone = getTimezone()

  const convocationConfig = selectedMedEvent?.convocation
  const shouldShowConvocationWarning =
    isEditing &&
    convocationConfig?.convocationOffset &&
    selectedMedEvent?.properties.some((property) => property.fieldName === 'convocationDate')

  if (!selectedMedEvent) return null

  return (
    <>
      <TimezoneInputSelector isProjectTimezone={isProjectTz} onToggle={handleToggleTimezone} />
      <div className="c-flex c-gap-4 c-flex-wrap c-items-end">
        {selectedMedEvent.properties?.map((property) => (
          <div
            key={`${getMedEventLabelById(selectedMedEvent.MedEvent.id)}-${property.fieldName}`}
            className="c-w-full c-max-w-[315px] c-mb-4"
          >
            {property.fieldName === 'UserId' && (
              <SelectMedicalStaffMember
                label={getTranslatedLabel(locale, property.label)}
                name={property.fieldName}
                isRequired={property.isRequired}
                projectId={patient?.ProjectId}
                referringDoctor={patient?.MedicalStaffMember}
                withDefaultValue={property.withDefaultValue}
              />
            )}
            {property.fieldName === 'LocationId' && (
              <SelectLocation
                label={getTranslatedLabel(locale, property.label)}
                name={property.fieldName}
                isRequired={property.isRequired}
                defaultLocationId={property.withDefaultValue ? defaultLocationId : undefined}
              />
            )}
            {property.fieldName === 'date' && (
              <AppointmentDatePicker
                label={getTranslatedLabel(locale, property.label)}
                name={property.fieldName}
                isRequired={property.isRequired}
                requiredErrorMessage={
                  property.isRequired
                    ? t('components.generic-followups-form.please-enter-date-and-hour')
                    : undefined
                }
                timezone={timezone}
              />
            )}
            {property.fieldName === 'convocationDate' && (
              <ConvocationDatePicker
                property={property}
                convocationConfig={convocationConfig}
                timezone={timezone}
              />
            )}
          </div>
        ))}
      </div>
      {shouldShowConvocationWarning && (
        <CurecallBanner
          appearance="INFO"
          className={twMerge('tw-w-full c-text-sm tw-py-2 [&>svg]:c-mx-4', errorClassName)}
        >
          {t('components.generic-followups-form.convocation-warning')}
        </CurecallBanner>
      )}
    </>
  )
}

export default MedEventFields
